/**
 * SaveStateContext - Provides save state management across components
 * 
 * This context provides access to the plan's save state, allowing components
 * to display save status, trigger saves, and respond to save events.
 */

import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';

// Create the context
const SaveStateContext = createContext({
    // Core save state
    isDirty: false,
    isSaving: false,
    lastSaveTime: null,
    lastError: null,
    
    // Actions
    markClean: () => {},
    markDirty: () => {},
    markSaveSuccess: () => {},
    markSaveError: () => {},
    setSaving: () => {},
    reset: () => {},
    
    // Utilities
    getSaveStatus: () => 'No changes',
    saveCount: 0,
    failureCount: 0,
    timeSinceLastSave: null,
    timeSinceLastChange: null,
    isOnline: true,
});

// Custom hook for consuming save state
export const useSaveState = () => {
    const context = useContext(SaveStateContext);
    if (!context) {
        throw new Error('useSaveState must be used within SaveStateProvider');
    }
    return context;
};

// Provider component
export const SaveStateProvider = ({ 
    children,
    saveState,
}) => {
    // Pass through the saveState object directly
    // This allows the hook's return value to be used directly
    const value = saveState || {
        isDirty: false,
        isSaving: false,
        lastSaveTime: null,
        lastError: null,
        markClean: () => {},
        markDirty: () => {},
        markSaveSuccess: () => {},
        markSaveError: () => {},
        setSaving: () => {},
        reset: () => {},
        getSaveStatus: () => 'No changes',
        saveCount: 0,
        failureCount: 0,
        timeSinceLastSave: null,
        timeSinceLastChange: null,
        isOnline: true,
    };
    
    return (
        <SaveStateContext.Provider value={value}>
            {children}
        </SaveStateContext.Provider>
    );
};

SaveStateProvider.propTypes = {
    children: PropTypes.node.isRequired,
    saveState: PropTypes.object,
};

export default SaveStateContext;